import React, { FC, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import CommonTitle from '@/components/Title';
import Mobile from '@/components/Device/Mobile';
import PC from '@/components/Device/PC';
import classNames from 'classnames';
import { Collapse } from '@kunukn/react-collapse';
import useResourcesStore from '@/store/useResourcesStore';
import { RootMenu, SubMenu, SubjectType } from '../../types';
import questionsEn from '../../questions_en.json';
import questionsZh from '../../questions_zh.json';

interface Props {
  menuData?: RootMenu[];
}

const Title: FC<Props> = ({ menuData }) => {
  const { t, i18n } = useTranslation(['resources']);
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const { subject } = router.query;
  const { filterTags } = useResourcesStore((state) => {
    return { filterTags: state.filterTags };
  });

  const titleMap = {
    chinese: `<strong>${t('国际中文资源')}</strong>`,
    math: `<strong>${t('国际数学资源')}</strong>`,
    english: `<strong>${t('英文读写资源')}</strong>`,
  };

  const questions = i18n.language === 'zh' ? questionsZh : questionsEn;
  const path = router.asPath.split('?')[0];
  const question: any = questions?.[path as keyof typeof questions];
  let menus: SubMenu[] = [];
  menuData?.forEach((item) => {
    menus = [...menus, ...item.subMenu];
  });
  const { describe } = menus?.find?.(({ value }) => path.includes(`/${value}/`)) || {};
  const subTitle = describe || question?.topDescription || '';

  const findTopTitle = (parentOrder: number) => {
    const { label, describeTopTitle } = filterTags?.find((item) => item.parentOrder === parentOrder) || {};
    return [label, describeTopTitle];
  };

  const getTitle = () => {
    const [label1, describeTopTitle1] = findTopTitle(1);
    const [label2, describeTopTitle2] = findTopTitle(2);
    const selectedTheme = filterTags?.find((item) => item.menuType === 'Theme')?.label || 'Resource';

    if (label1 && label2) {
      return i18n.language === 'en'
        ? `<strong>${label2} ${label1} ${selectedTheme}</strong>`
        : titleMap[subject as SubjectType];
    }

    if (describeTopTitle1) {
      return `<strong>${describeTopTitle1}</strong>`;
    }

    if (describeTopTitle2) {
      return `<strong>${describeTopTitle2}</strong>`;
    }

    return titleMap[subject as SubjectType];
  };

  return (
    <div className="pb-[24px] pt-[50px] lg:pb-[32px] lg:pt-[40px] xl:pt-[48px]">
      <CommonTitle htmlLabel="h1" className="flex justify-center lg:justify-start" innerHTML theme="colorful">
        {getTitle()}
      </CommonTitle>
      {!!subTitle && (
        <>
          <Mobile>
            <Collapse
              isOpen={open}
              collapseHeight={`${52 / 16}rem`}
              transition="height 300ms cubic-bezier(0.4, 0, 0.2, 1)"
              className="text-center lg:text-left"
            >
              <p
                className={classNames(
                  'text-[14px] not-italic leading-[22px] text-[#8D8FA6] lg:leading-[24px] xl:text-[16px] xl:leading-[28px] mobile:pt-[8px] pc:pt-[10px]',
                  // !open && 'line-clamp-2',
                )}
                dangerouslySetInnerHTML={{ __html: subTitle }}
              />
            </Collapse>
            <span
              onClick={() => setOpen(!open)}
              className="mt-[4px] block text-center text-[14px] leading-[22px] text-wk-words-2 underline lg:text-left"
            >
              {open ? t('收起') : t('阅读更多')}
            </span>
          </Mobile>
          <PC>
            <p
              className="not-italic text-[#8D8FA6] mobile:pt-[8px] mobile:text-[14px] mobile:leading-[22px] pc:pt-[10px] pc:text-[16px] pc:leading-[28px]"
              dangerouslySetInnerHTML={{ __html: subTitle }}
            />
          </PC>
        </>
      )}
    </div>
  );
};

export default Title;
